import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";
import { DirectUpload } from "@rails/activestorage";

const MAX_TOTAL_SIZE = 200 * 1024 * 1024;
const MAX_LOGO_SIZE = 10 * 1024 * 1024;
const ALLOWED_ASSET_TYPES = ["image/jpeg", "image/png", "video/mp4", "video/quicktime"];
const ALLOWED_LOGO_TYPES = ["image/jpeg", "image/png"];

// Supported project aspect ratios — file ratios are bucketed to whichever
// of these is numerically closest, so laypeople only see a mismatch when
// their asset's *orientation family* differs (e.g. landscape vs square),
// not for small deviations from an exact ratio they never chose.
const SUPPORTED_RATIOS = { "16:9": 16 / 9, "9:16": 9 / 16, "1:1": 1, "4:5": 4 / 5 };

export default class extends Controller {
  static targets = ["assetsInput", "assetsPreview", "assetsZone", "reviewGrid", "summary", "logoInput", "logoPreview", "logoZone", "meter", "meterText", "targetDuration", "mismatchBanner", "selectedHeader", "selectedCount", "logoNotice", "logoBrandName", "logoThumb", "logoIcon"];

  static values = {
    imageClipSeconds: { type: Number, default: 3.0 },
    existingAssets: { type: Array, default: [] },
    i18nZoneEmpty: { type: String, default: "Click or drag files here" },
    i18nZoneSelected: { type: String, default: "{count} files selected · {size} — click to add more" },
    i18nZoneSelectedOne: { type: String, default: "1 file selected · {size} — click to add more" },
    i18nMeterEmpty: { type: String, default: "Add assets to see a rough total duration." },
    i18nMeterLow: { type: String, default: "≈ {total} of material for a {target}s video — a bit light." },
    i18nMeterOver: { type: String, default: "≈ {total} of material for a {target}s video — more than needed." },
    i18nMeterOk: { type: String, default: "≈ {total} of material for a {target}s video — looking good." },
    i18nMeterNote: { type: String, default: "Output will always match the selected duration exactly." },
    i18nReviewEmpty: { type: String, default: "No assets added yet." },
    i18nLogoRemove: { type: String, default: "Remove" },
    i18nAddMore: { type: String, default: "Add more files" },
    i18nErrorRemoveFailed: { type: String, default: "Remove failed uploads before continuing." },
    i18nErrorUploadFailed: { type: String, default: "Some uploads failed. Remove them and try again." },
    i18nErrorTimeout: { type: String, default: "Upload timed out. Check your connection and try again." },
    i18nUploading: { type: String, default: "Uploading…" },
    i18nRowName: { type: String, default: "Name" },
    i18nRowDuration: { type: String, default: "Duration" },
    i18nRowOrientation: { type: String, default: "Orientation" },
    i18nRowAssets: { type: String, default: "Assets" },
    i18nRowHeadline: { type: String, default: "Headline" },
    i18nRowDescription: { type: String, default: "Description" },
    i18nRowFont: { type: String, default: "Font" },
    i18nRowTextAnimation: { type: String, default: "Text animation" },
    i18nRowTransition: { type: String, default: "Transition" },
    i18nRowLogoPosition: { type: String, default: "Logo position" },
    i18nRowBgm: { type: String, default: "Background music" },
    i18nRowPresetDefault: { type: String, default: "Preset default" },
    i18nAssetsCount: { type: String, default: "{count} files" },
    i18nAssetsCountOne: { type: String, default: "1 file" },
    i18nErrorTotalSize: { type: String, default: "Total upload size exceeds {max}." },
    i18nErrorLogoInvalidFormat: { type: String, default: "Logo must be a JPG or PNG file." },
    i18nErrorAssetInvalidFormat: { type: String, default: "Assets must be JPEG, PNG, MP4, or MOV." },
    i18nAssetRatioMismatch: { type: String, default: "This asset's aspect ratio doesn't match the project — expect black bars (letterboxing)." },
    i18nRatioMismatchTitle: { type: String, default: "Aspect ratio mismatch" },
    i18nRatioMismatchConfirm: { type: String, default: "Continue anyway" },
  };

  #assetFiles = [];
  #blobIds = new Map(); // file key → signed blob ID
  #failedKeys = new Set(); // file keys whose upload failed
  #assetDims = new Map(); // key: "name|size" → { w, h }
  #mismatchKeys = new Set(); // file keys whose aspect ratio doesn't match the project
  #mismatchAcknowledged = false;
  #mismatchModalOpened = false; // avoid re-prompting for the same mismatch batch
  #videoDurations = new Map(); // key: "name|size" → duration seconds
  #removedExistingIds = new Set(); // saved project_asset ids the user deleted

  connect() {
    if (this.hasAssetsPreviewTarget) {
      this.sortable = Sortable.create(this.assetsPreviewTarget, {
        animation: 150,
        // contained, symmetric reflow of multiple items — M3 standard easing
        easing: "cubic-bezier(0.2, 0, 0, 1)",
        ghostClass: "upload-thumb--ghost",
        onEnd: (e) => this.#onSortEnd(e),
      });
    }
    this.#seedExistingAssets();
    this.#renderAll();

    this._onBeforeSubmit = (e) => {
      // Block if any upload failed — user must remove failed files first
      if (this.#failedKeys.size > 0) {
        e.preventDefault();
        this.#setSubmitLabel(this.#nextBtnEl()?.dataset.labelSubmit ?? "Generate video");
        this.#toast(this.i18nErrorRemoveFailedValue, "error");
        return;
      }

      // Brand picks are already in storage — only real uploads can be pending.
      const pending = this.#assetFiles.filter(
        (f) => !this.#isStored(f) && !this.#blobIds.has(this.#key(f)) && !this.#failedKeys.has(this.#key(f))
      );

      if (pending.length > 0) {
        e.preventDefault();
        const total = this.#assetFiles.length;
        this.#setSubmitLabel(`${this.i18nUploadingValue} ${total - pending.length} / ${total}`);
        this.#waitForUploads()
          .then(() => {
            this.#setSubmitLabel(this.#nextBtnEl()?.dataset.labelSubmit ?? "Generate video");
            this.element.requestSubmit();
          })
          .catch((msg) => {
            this.#setSubmitLabel(this.#nextBtnEl()?.dataset.labelSubmit ?? "Generate video");
            this.#toast(msg, "error");
          });
        return;
      }

      this.#injectBlobInputs();
    };
    this.element.addEventListener("submit", this._onBeforeSubmit);
  }

  disconnect() {
    if (this.sortable) this.sortable.destroy();
    this.element.removeEventListener("submit", this._onBeforeSubmit);
  }

  // ---- Assets ----

  previewAssets() {
    const incoming = Array.from(this.assetsInputTarget.files);
    // Clear immediately — direct upload handles transfer; we never want
    // the raw file submitted with the form (causes duplicate + hang).
    this.assetsInputTarget.value = "";
    this.#addFiles(incoming);
  }

  dragover(e) {
    e.preventDefault();
    this.assetsZoneTarget.classList.add("upload-zone--dragover");
  }

  dragoverLogo(e) {
    e.preventDefault();
    this.logoZoneTarget.classList.add("logo-dropzone--dragover");
  }

  dropLogo(e) {
    e.preventDefault();
    this.logoZoneTarget.classList.remove("logo-dropzone--dragover");
    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      this.#toast(this.i18nErrorLogoInvalidFormatValue, "error");
      return;
    }

    const dt = new DataTransfer();
    dt.items.add(file);
    this.logoInputTarget.files = dt.files;
    this.previewLogo();
  }

  dragleave() {
    this.assetsZoneTarget?.classList.remove("upload-zone--dragover");
    this.logoZoneTarget?.classList.remove("logo-dropzone--dragover");
  }

  // Called by brand-picker when a gallery tile is toggled. The descriptor is
  // shaped to satisfy the same .name/.size/.type interface a File has, so it
  // can join #assetFiles directly instead of living in a parallel list.
  addBrandAsset(e) {
    const d = e.detail;
    const item = {
      brandAssetId: d.id,
      name: d.name,
      size: d.size,
      type: d.contentType,
      url: d.url,
      thumbUrl: d.thumbUrl,
      posterUrl: d.posterUrl
    };
    const key = this.#key(item);
    if (this.#assetFiles.some((f) => this.#key(f) === key)) return;

    // Dimensions and duration come from the server, so the mismatch check and
    // the duration meter work immediately — no client-side probing needed.
    if (d.width && d.height) this.#assetDims.set(key, { w: d.width, h: d.height });
    if (d.durationMs) this.#videoDurations.set(key, d.durationMs / 1000);

    this.#assetFiles.push(item);
    this.#renderAll();
    this.#recomputeMismatch(item);
  }

  removeBrandAsset(e) {
    const id = e.detail.id;
    // The clip may be a fresh pick (brand:<id>) or one already saved on the
    // draft from an earlier visit — both have to answer to the same tile.
    const index = this.#assetFiles.findIndex(
      (f) => this.#key(f) === `brand:${id}` || f.sourceBrandAssetId === id
    );
    if (index === -1) return;

    const [removed] = this.#assetFiles.splice(index, 1);
    const key = this.#key(removed);
    if (this.#isExisting(removed)) this.#removedExistingIds.add(removed.projectAssetId);
    this.#assetDims.delete(key);
    this.#videoDurations.delete(key);
    this.#mismatchKeys.delete(key);
    if (this.#mismatchKeys.size === 0) {
      this.#mismatchAcknowledged = false;
      this.#mismatchModalOpened = false;
    }
    this.#renderAll();
  }

  dropAssets(e) {
    e.preventDefault();
    this.assetsZoneTarget.classList.remove("upload-zone--dragover");
    this.#addFiles(Array.from(e.dataTransfer.files));
  }

  removeAsset(e) {
    e.stopPropagation();
    const index = parseInt(e.currentTarget.dataset.index, 10);
    const entry = this.#assetFiles[index];

    const commit = () => {
      const at = this.#assetFiles.indexOf(entry);
      if (at === -1) return;
      const [removed] = this.#assetFiles.splice(at, 1);
      // Removing a brand tile here has to clear the picker's selection too,
      // or the tile could never be re-added.
      if (this.#isBrand(removed)) {
        this.dispatch("brandAssetRemoved", {
          detail: { id: removed.brandAssetId }, target: window, prefix: "upload-preview"
        });
      }
      // Dropping a saved clip only marks it; the row is deleted when the
      // wizard is submitted.
      if (this.#isExisting(removed)) this.#removedExistingIds.add(removed.projectAssetId);
      this.#blobIds.delete(this.#key(removed));
      this.#failedKeys.delete(this.#key(removed));
      if (removed) this.#videoDurations.delete(this.#key(removed));
      this.#mismatchKeys.delete(this.#key(removed));
      this.#assetDims.delete(this.#key(removed));
      if (this.#mismatchKeys.size === 0) {
        this.#mismatchAcknowledged = false;
        this.#mismatchModalOpened = false;
      }
      this.#renderAll();
    };

    const thumb = e.currentTarget.closest(".filmstrip__item");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!thumb || reduceMotion) {
      commit();
      return;
    }

    // The exit animates opacity (100ms) *and* width (150ms), so the first
    // transitionend to arrive is opacity's — a `{ once: true }` listener is
    // spent on it and the removal never commits, leaving the tile collapsed
    // but still in #assetFiles (stale count/size, and it reappears on the
    // next render). Wait for the tile's own width transition, with a timer
    // in case the event never arrives (interrupted or dropped transition).
    let committed = false;
    const commitOnce = () => {
      if (committed) return;
      committed = true;
      thumb.removeEventListener("transitionend", onEnd);
      commit();
    };
    const onEnd = (ev) => {
      if (ev.target === thumb && ev.propertyName === "width") commitOnce();
    };
    thumb.addEventListener("transitionend", onEnd);
    setTimeout(commitOnce, 400);
    requestAnimationFrame(() => thumb.classList.add("filmstrip__item--exiting"));
  }

  recomputeAllMismatches() {
    this.#assetFiles.forEach((f) => this.#recomputeMismatch(f));
  }

  reviewMismatch(e) {
    e?.preventDefault();
    this.#openMismatchModal();
  }

  onDurationChange() {
    this.#renderMeter();
  }

  // ---- Logo ----

  openLogo() {
    this.logoInputTarget.click();
  }

  // ---- Brand logo notice ----
  //
  // The notice sits in the logo block, which brand-picker's section does not
  // enclose, so the picker hands its state over the window and this controller
  // renders it.

  onBrandLogo({ detail }) {
    if (!this.hasLogoNoticeTarget) return;

    if (detail.active) {
      if (this.hasLogoBrandNameTarget) this.logoBrandNameTarget.textContent = detail.name;
      this.#renderBrandLogoThumb(detail.logoUrl);
    }
    this.logoNoticeTarget.hidden = !detail.active;
    this.#syncLogoZone();
  }

  // Showing the actual mark makes "using logo from X" checkable at a glance.
  // Brands whose logo has no variant fall back to the info dot.
  #renderBrandLogoThumb(url) {
    if (!this.hasLogoThumbTarget) return;

    if (url) this.logoThumbTarget.querySelector("img").src = url;
    this.logoThumbTarget.hidden = !url;
    if (this.hasLogoIconTarget) this.logoIconTarget.hidden = Boolean(url);
  }

  dropBrandLogo() {
    this.#hideBrandLogoNotice();
    this.dispatch("brandLogoDropped", { target: window });
  }

  #hideBrandLogoNotice() {
    if (this.hasLogoNoticeTarget) this.logoNoticeTarget.hidden = true;
    this.#syncLogoZone();
  }

  #syncLogoZone() {
    if (!this.hasLogoZoneTarget) return;
    const brandLogoInUse = this.hasLogoNoticeTarget && !this.logoNoticeTarget.hidden;
    this.logoZoneTarget.hidden = brandLogoInUse || this.logoInputTarget.files.length > 0;
  }

  previewLogo() {
    const file = this.logoInputTarget.files[0];
    if (!file) {
      this.logoPreviewTarget.innerHTML = "";
      return;
    }
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      this.#toast(this.i18nErrorLogoInvalidFormatValue, "error");
      this.logoInputTarget.value = "";
      return;
    }
    if (file.size > MAX_LOGO_SIZE) {
      this.#toast(`${file.name} is ${this.#fmtBytes(file.size)} — logo must be under ${this.#fmtBytes(MAX_LOGO_SIZE)}.`, "error");
      this.logoInputTarget.value = "";
      return;
    }

    if (this.hasLogoZoneTarget) this.logoZoneTarget.hidden = true;
    if (this.hasLogoNoticeTarget && !this.logoNoticeTarget.hidden) this.dropBrandLogo();

    const reader = new FileReader();
    reader.onload = (e) => {
      this.logoPreviewTarget.innerHTML = `
        <div class="logo-preview-card" style="margin-top:var(--sp-3);display:flex;align-items:center;
                    gap:var(--sp-3);padding:var(--sp-3) var(--sp-4);
                    background:var(--surface);border:1px solid var(--border);border-radius:var(--r-md);">
          <img src="${e.target.result}" alt="Logo preview"
               style="width:48px;height:48px;object-fit:contain;flex-shrink:0;">
          <div style="flex:1;min-width:0;">
            <div style="font-size:13px;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
              ${file.name}
            </div>
            <div style="font-size:12px;color:var(--slate);margin-top:2px;">
              ${this.#fmtBytes(file.size)}
            </div>
          </div>
          <button type="button" class="btn btn--ghost btn--sm"
                  aria-label="Remove logo"
                  data-action="click->upload-preview#removeLogo">${this.i18nLogoRemoveValue}</button>
        </div>
      `;
    };
    reader.readAsDataURL(file);
  }

  removeLogo() {
    this.logoInputTarget.value = "";
    this.#syncLogoZone();

    const card = this.logoPreviewTarget.querySelector(".logo-preview-card");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!card || reduceMotion) {
      this.logoPreviewTarget.innerHTML = "";
      return;
    }

    // M3 motion: small on-screen exit — standard-accelerate, short2/short3
    const height = card.offsetHeight;
    card.style.height = `${height}px`;
    card.style.overflow = "hidden";
    card.style.transition =
      "opacity var(--m3-dur-short2) var(--m3-standard-accelerate), " +
      "height var(--m3-dur-short3) var(--m3-standard-accelerate), " +
      "margin-top var(--m3-dur-short3) var(--m3-standard-accelerate), " +
      "padding var(--m3-dur-short3) var(--m3-standard-accelerate)";

    card.addEventListener(
      "transitionend",
      (e) => { if (e.propertyName === "height") this.logoPreviewTarget.innerHTML = ""; },
      { once: true }
    );

    requestAnimationFrame(() => {
      card.style.opacity = "0";
      requestAnimationFrame(() => {
        card.style.height = "0px";
        card.style.marginTop = "0px";
        card.style.paddingTop = "0px";
        card.style.paddingBottom = "0px";
      });
    });
  }

  // ---- Preview (dispatch to shared modal) ----

  openPreview(e) {
    const thumb = e.currentTarget;
    const index = parseInt(thumb.dataset.index, 10);
    const file = this.#assetFiles[index];
    if (!file) return;

    const type = (file.type || "").startsWith("image/") ? "image" : "video";

    // Stored clips are descriptors, not Blobs — they already have a server
    // URL, and handing one to createObjectURL throws. Videos preview from
    // `url`, never `posterUrl`: the poster is only the thumbnail.
    if (this.#isStored(file)) {
      if (!file.url) return;
      this.dispatch("open", {
        prefix: "preview-modal",
        target: window,
        detail: { url: file.url, type },
      });
      return;
    }

    this.dispatch("open", {
      prefix: "preview-modal",
      target: window,
      detail: { url: URL.createObjectURL(file), type, revokeOnClose: true },
    });
  }

  // ---- Private ----

  // Brand picks are keyed by record id; uploaded files by name+size. Both
  // live in #assetFiles so every aggregate (meter, review grid, summary,
  // mismatch gate, drag order) counts them without knowing the difference.
  #key(file) {
    if (this.#isBrand(file)) return `brand:${file.brandAssetId}`;
    if (this.#isExisting(file)) return `existing:${file.projectAssetId}`;
    return `${file.name}|${file.size}`;
  }

  #isBrand(file) {
    return file?.brandAssetId != null;
  }

  // Clips already saved on the draft. Like brand picks they are "virtual
  // files": stored server-side, so nothing to upload or probe, but they take
  // part in ordering, the duration meter, the review count and the ratio gate.
  #isExisting(file) {
    return file?.projectAssetId != null;
  }

  #isStored(file) {
    return this.#isBrand(file) || this.#isExisting(file);
  }

  // Brand clips reuse the brand's stored file, so they are not uploads and
  // don't count toward the upload size limit (Project skips them too).
  #isFromBrand(file) {
    return this.#isBrand(file) || file?.sourceBrandAssetId != null;
  }

  #seedExistingAssets() {
    this.existingAssetsValue.forEach((d) => {
      const item = {
        projectAssetId: d.id,
        name: d.name,
        size: d.size,
        type: d.contentType,
        url: d.url,
        thumbUrl: d.thumbUrl,
        posterUrl: d.posterUrl,
        // Set when this clip was copied from a brand gallery item, so the
        // picker can show it ticked and unticking it can remove it again.
        sourceBrandAssetId: d.brandAssetId
      };
      const key = this.#key(item);
      if (d.width && d.height) this.#assetDims.set(key, { w: d.width, h: d.height });
      if (d.durationMs) this.#videoDurations.set(key, d.durationMs / 1000);
      this.#assetFiles.push(item);
    });
  }

  #addFiles(rawIncoming) {
    const incoming = rawIncoming.filter((f) => ALLOWED_ASSET_TYPES.includes(f.type));
    const rejected = rawIncoming.filter((f) => !ALLOWED_ASSET_TYPES.includes(f.type));

    if (rejected.length > 0) {
      this.#toast(this.i18nErrorAssetInvalidFormatValue, "error");
    }
    if (incoming.length === 0) return;

    const currentTotal = this.#assetFiles.filter((f) => !this.#isFromBrand(f)).reduce((s, f) => s + f.size, 0);
    const incomingTotal = incoming.reduce((s, f) => s + f.size, 0);
    if (currentTotal + incomingTotal > MAX_TOTAL_SIZE) {
      this.#toast(`Total upload size exceeds ${this.#fmtBytes(MAX_TOTAL_SIZE)}.`, "error");
      return;
    }
    incoming.forEach((file) => {
      const exists = this.#assetFiles.some((f) => this.#key(f) === this.#key(file));
      if (exists) return;
      this.#assetFiles.push(file);
      const url = this.assetsInputTarget?.dataset?.directUploadUrl;
      if (url) this.#uploadFile(file, url);
      if (file.type.startsWith("video/")) {
        this.#probeVideoDuration(file);
      }
      if (file.type.startsWith("image/")) {
        this.#probeImageDims(file);
      }
    });
    this.#renderAll();
  }

  #probeImageDims(file) {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      this.#assetDims.set(this.#key(file), { w: img.naturalWidth, h: img.naturalHeight });
      URL.revokeObjectURL(url);
      this.#recomputeMismatch(file);
    };
    img.onerror = () => URL.revokeObjectURL(url);
    img.src = url;
  }

  #targetRatioKey() {
    const form = this.element.tagName === "FORM" ? this.element : this.element.closest("form");
    const scope = form || document;
    const checked = scope.querySelector('input[name="project[aspect_ratio]"]:checked');
    return checked ? checked.value : null;
  }

  // Nearest of the 4 supported ratios by plain absolute distance — cheap,
  // no logs/trig, runs once per asset on image load.
  #nearestRatioKey(ratio) {
    let bestKey = null;
    let bestDelta = Infinity;
    for (const [key, value] of Object.entries(SUPPORTED_RATIOS)) {
      const delta = Math.abs(ratio - value);
      if (delta < bestDelta) {
        bestDelta = delta;
        bestKey = key;
      }
    }
    return bestKey;
  }

  #recomputeMismatch(file) {
    const targetKey = this.#targetRatioKey();
    const dims = this.#assetDims.get(this.#key(file));
    if (!targetKey || !dims || !dims.w || !dims.h) return;

    const fileRatio = dims.w / dims.h;
    // Bucket the file's ratio to whichever supported ratio it's closest to.
    // Only flag a mismatch when it lands in a different bucket than the
    // project's selected ratio — matches how laypeople think (landscape /
    // portrait / square / 4:5), not exact-ratio math.
    const isMismatch = this.#nearestRatioKey(fileRatio) !== targetKey;

    const key = this.#key(file);
    if (isMismatch) this.#mismatchKeys.add(key);
    else this.#mismatchKeys.delete(key);

    if (this.#mismatchKeys.size === 0) {
      this.#mismatchAcknowledged = false;
      this.#mismatchModalOpened = false;
    }

    // Patch just this file's badge in place — do NOT call #renderAll() here.
    // It wipes and rebuilds the whole filmstrip (incl. regenerating every
    // video thumbnail from scratch), and this runs once per asset as probes
    // resolve async — with N videos that's an O(N²) thumbnail regeneration
    // storm that can hang/crash the tab.
    this.#patchMismatchBadge(file, key);
    this.#renderMismatchBanner();

    if (this.#mismatchKeys.size > 0 && !this.#mismatchAcknowledged && !this.#mismatchModalOpened) {
      this.#mismatchModalOpened = true;
      this.#openMismatchModal();
    }
  }

  #patchMismatchBadge(file, key) {
    const isMismatch = this.#mismatchKeys.has(key);
    this.element.querySelectorAll(`[data-file-key="${CSS.escape(key)}"]`).forEach((item) => {
      item.classList.toggle("filmstrip__item--mismatch", isMismatch);
      item.title = isMismatch ? this.i18nAssetRatioMismatchValue : "";
    });
  }

  #openMismatchModal() {
    window.dispatchEvent(new CustomEvent("confirm-dialog:open", {
      detail: {
        title: this.i18nRatioMismatchTitleValue,
        message: this.i18nAssetRatioMismatchValue,
        confirmLabel: this.i18nRatioMismatchConfirmValue,
        onConfirm: () => {
          this.#mismatchAcknowledged = true;
          this.#renderMismatchBanner();
        },
      },
    }));
  }

  #probeVideoDuration(file) {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.src = url;
    video.addEventListener(
      "loadedmetadata",
      () => {
        const d = isFinite(video.duration) ? video.duration : 0;
        this.#videoDurations.set(this.#key(file), d);
        this.#assetDims.set(this.#key(file), { w: video.videoWidth, h: video.videoHeight });
        URL.revokeObjectURL(url);
        this.#updateDurationBadges(file, d);
        this.#renderMeter();
        this.#recomputeMismatch(file);
      },
      { once: true },
    );
    video.addEventListener("error", () => URL.revokeObjectURL(url), { once: true });
  }

  #updateDurationBadges(file, seconds) {
    const key = this.#key(file);
    this.element.querySelectorAll(`[data-duration-key="${CSS.escape(key)}"]`).forEach((badge) => {
      badge.textContent = this.#fmtSec(seconds);
    });
  }

  #onSortEnd(e) {
    const [moved] = this.#assetFiles.splice(e.oldIndex, 1);
    this.#assetFiles.splice(e.newIndex, 0, moved);

    // Sortable already moved the DOM nodes — just re-tag indices, don't
    // rebuild thumbs (that would re-decode every image/video from scratch).
    this.assetsPreviewTarget
      .querySelectorAll(".filmstrip__item[data-file-key]")
      .forEach((item, i) => {
        item.dataset.index = i;
        const removeBtn = item.querySelector(".filmstrip__remove");
        if (removeBtn) removeBtn.dataset.index = i;
      });

    this.#renderReviewGrid();
    this.#renderSummary();
    this.#renderMeter();
    this.#renderMismatchBanner();
  }

  #renderAll() {
    this.#renderSelectedHeader();
    this.#renderAssetPreviews();
    this.#renderReviewGrid();
    this.#renderSummary();
    this.#renderMeter();
    this.#renderMismatchBanner();
  }

  // "SELECTED ASSETS (5)" — hidden entirely while the strip is empty so the
  // step opens on the sources rather than an empty labelled box.
  #renderSelectedHeader() {
    if (!this.hasSelectedHeaderTarget) return;

    const count = this.#assetFiles.length;
    this.selectedHeaderTarget.hidden = count === 0;
    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent =
        this.selectedCountTarget.dataset.template.replace("%{count}", count);
    }
  }

  #renderMismatchBanner() {
    const pending = this.#mismatchKeys.size > 0 && !this.#mismatchAcknowledged;
    if (this.hasMismatchBannerTarget) {
      this.mismatchBannerTarget.hidden = !pending;
      this.mismatchBannerTarget.style.display = pending ? "flex" : "none";
    }
    this.element.dataset.ratioMismatchPending = pending ? "true" : "false";
    this.dispatch("mismatchChanged", { detail: { pending } });
  }

  #renderAssetPreviews() {
    if (!this.hasAssetsPreviewTarget) return;

    this.assetsPreviewTarget.innerHTML = "";

    this.#assetFiles.forEach((file, i) => {
      const thumb = this.#buildThumb(file, i, { removable: true });
      this.assetsPreviewTarget.appendChild(thumb);
    });

    if (this.#assetFiles.length > 0) {
      const addTile = document.createElement("div");
      addTile.className = "filmstrip__item";
      addTile.style.cssText = "border-style:dashed;cursor:pointer;";
      addTile.setAttribute("aria-label", this.i18nAddMoreValue);
      addTile.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3f3f3f" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>`;
      addTile.addEventListener("click", () => this.assetsInputTarget.click());
      this.assetsPreviewTarget.appendChild(addTile);
    }

    if (this.hasAssetsZoneTarget) {
      const title = this.assetsZoneTarget.querySelector(".upload-zone__title");
      if (title) {
        const uploads = this.#assetFiles.filter((f) => !this.#isFromBrand(f));
        const total = uploads.reduce((s, f) => s + f.size, 0);
        if (uploads.length === 0) {
          title.textContent = this.i18nZoneEmptyValue;
        } else if (uploads.length === 1) {
          title.textContent = this.i18nZoneSelectedOneValue.replace("{size}", this.#fmtBytes(total));
        } else {
          title.textContent = this.i18nZoneSelectedValue.replace("{count}", uploads.length).replace("{size}", this.#fmtBytes(total));
        }
      }
    }
  }

  #renderReviewGrid() {
    if (!this.hasReviewGridTarget) return;
    this.reviewGridTarget.innerHTML = "";
    if (this.#assetFiles.length === 0) {
      this.reviewGridTarget.innerHTML = `
        <div class="review-empty">${this.i18nReviewEmptyValue}</div>
      `;
      return;
    }
    this.#assetFiles.forEach((file, i) => {
      const thumb = this.#buildThumb(file, i, { removable: false });
      this.reviewGridTarget.appendChild(thumb);
    });
  }

  refreshSummary() {
    this.#renderSummary();
  }

  #renderSummary() {
    if (!this.hasSummaryTarget) return;
    const form = this.element.tagName === "FORM" ? this.element : this.element.closest("form");
    const scope = form || document;
    const val = (name) => {
      const el = scope.querySelector(`[name="project[${name}]"]`);
      if (!el) return "—";
      if (el.type === "radio") {
        const checked = scope.querySelector(`[name="project[${name}]"]:checked`);
        return checked ? checked.value : "—";
      }
      if (el.tagName === "SELECT") {
        const opt = el.options[el.selectedIndex];
        return opt && opt.value ? opt.textContent.trim() : el.value || "—";
      }
      return el.value || "—";
    };

    const name = val("name");
    const duration = val("duration_seconds");
    const orientation = val("orientation");
    const headline = val("headline");
    const description = val("description");
    const font = val("font_style");
    const textAnim = val("text_animation");
    const transition = val("transition_type");
    const logoPos = val("logo_position");

    const bgmSelect = scope.querySelector(`[name="project[bgm_track_id]"]`);
    let bgm = "—";
    if (bgmSelect && bgmSelect.value) {
      const opt = bgmSelect.options[bgmSelect.selectedIndex];
      if (opt) bgm = opt.textContent.trim();
    }

    const row = (label, value) => `
      <div class="review-summary__row">
        <span class="review-summary__key">${label}</span>
        <span class="review-summary__val">${value || "—"}</span>
      </div>`;

    this.summaryTarget.innerHTML = [
      row(this.i18nRowNameValue, this.#esc(name)),
      row(this.i18nRowDurationValue, duration !== "—" ? `${duration}s` : "—"),
      row(this.i18nRowOrientationValue, orientation !== "—" ? this.#humanize(orientation) : "—"),
      row(this.i18nRowAssetsValue, this.#assetFiles.length === 1 ? this.i18nAssetsCountOneValue : this.i18nAssetsCountValue.replace("{count}", this.#assetFiles.length)),
      row(this.i18nRowHeadlineValue, this.#esc(headline)),
      row(this.i18nRowDescriptionValue, this.#esc(description)),
      row(this.i18nRowFontValue, font !== "—" ? this.#humanize(font) : this.i18nRowPresetDefaultValue),
      row(this.i18nRowTextAnimationValue, textAnim !== "—" ? this.#humanize(textAnim) : this.i18nRowPresetDefaultValue),
      row(this.i18nRowTransitionValue, transition !== "—" ? this.#humanize(transition) : this.i18nRowPresetDefaultValue),
      row(this.i18nRowLogoPositionValue, logoPos !== "—" ? this.#humanize(logoPos) : "—"),
      row(this.i18nRowBgmValue, this.#esc(bgm)),
    ].join("");
  }

  #renderMeter() {
    if (!this.hasMeterTextTarget) return;
    const total = this.#roughTotalSeconds();
    const target = this.#targetSeconds();

    if (this.#assetFiles.length === 0) {
      this.meterTextTarget.innerHTML = `<span class="meter__msg">${this.i18nMeterEmptyValue}</span>`;
      if (this.hasMeterTarget) this.meterTarget.dataset.state = "empty";
      return;
    }

    const pct = target > 0 ? total / target : 0;
    let msg, state;
    if (pct < 0.8) {
      msg = this.i18nMeterLowValue.replace("{total}", this.#fmtSec(total)).replace("{target}", target);
      state = "low";
    } else if (pct > 1.2) {
      msg = this.i18nMeterOverValue.replace("{total}", this.#fmtSec(total)).replace("{target}", target);
      state = "over";
    } else {
      msg = this.i18nMeterOkValue.replace("{total}", this.#fmtSec(total)).replace("{target}", target);
      state = "ok";
    }

    this.meterTextTarget.innerHTML = `
      <span class="meter__msg">${msg}</span>
      <span class="meter__note">${this.i18nMeterNoteValue}</span>
    `;
    if (this.hasMeterTarget) this.meterTarget.dataset.state = state;
  }

  #roughTotalSeconds() {
    return this.#assetFiles.reduce((sum, f) => {
      if (f.type.startsWith("video/")) {
        const d = this.#videoDurations.get(this.#key(f));
        return sum + (d || 0);
      }
      return sum + this.imageClipSecondsValue;
    }, 0);
  }

  #targetSeconds() {
    const form = this.element.tagName === "FORM" ? this.element : this.element.closest("form");
    const scope = form || document;
    const checked = scope.querySelector('input[name="project[duration_seconds]"]:checked');
    return checked ? parseInt(checked.value, 10) : 30;
  }

  #buildThumb(file, i, { removable }) {
    const isImage = file.type.startsWith("image/");
    const key = this.#key(file);

    const item = document.createElement("div");
    item.className = "filmstrip__item";
    item.dataset.index = i;
    item.dataset.fileKey = key;
    item.style.cursor = "pointer";
    item.dataset.action = "click->upload-preview#openPreview";
    item.setAttribute("aria-label", `Preview ${file.name}`);

    // Upload progress ring — hidden once the blob id arrives. Clips that are
    // already in storage (brand picks, saved draft clips) have nothing to
    // upload, so the ring must never show for them or it spins forever.
    const ring = document.createElement("div");
    ring.className = "filmstrip__progress";
    ring.hidden = this.#isStored(file) || this.#blobIds.has(key);
    item.appendChild(ring);

    // Brand picks are already stored server-side, so the thumbnail is just a
    // URL — no FileReader decode or canvas frame-grab needed.
    if (this.#isStored(file)) {
      const img = document.createElement("img");
      img.src = file.posterUrl || file.thumbUrl || file.url;
      img.alt = file.name;
      img.loading = "lazy";
      img.style.cssText = "width:100%;height:100%;object-fit:cover;position:absolute;inset:0;";
      item.appendChild(img);
    } else if (isImage) {
      const img = document.createElement("img");
      img.alt = file.name;
      img.style.cssText = "width:100%;height:100%;object-fit:cover;position:absolute;inset:0;";
      const reader = new FileReader();
      reader.onload = (ev) => {
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
      item.appendChild(img);
    } else {
      this.#generateVideoThumb(file, item);
    }

    // Badge
    const badge = document.createElement("span");
    badge.className = "filmstrip__badge";
    badge.setAttribute("aria-hidden", "true");
    badge.textContent = isImage ? "IMG" : "VID";
    item.appendChild(badge);

    if (this.#mismatchKeys.has(key)) {
      item.classList.add("filmstrip__item--mismatch");
      item.title = this.i18nAssetRatioMismatchValue;
    }

    if (removable) {
      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className = "filmstrip__remove";
      removeBtn.setAttribute("aria-label", `Remove ${file.name}`);
      removeBtn.dataset.index = i;
      removeBtn.dataset.action = "click->upload-preview#removeAsset";
      removeBtn.innerHTML = `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
      item.appendChild(removeBtn);
    }

    return item;
  }

  #generateVideoThumb(file, thumb) {
    thumb.style.background = "var(--bg-inset)";

    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    video.src = url;

    const cleanup = () => URL.revokeObjectURL(url);

    video.addEventListener("loadedmetadata", () => {
      const target = Math.min(1, video.duration * 0.1);
      video.currentTime = isFinite(target) ? target : 0;
    });

    video.addEventListener(
      "seeked",
      () => {
        const canvas = document.createElement("canvas");
        const THUMB_MAX = 480;
        const vw = video.videoWidth || 320;
        const vh = video.videoHeight || 180;
        const scale = Math.min(1, THUMB_MAX / Math.max(vw, vh));
        canvas.width = Math.round(vw * scale);
        canvas.height = Math.round(vh * scale);
        const ctx = canvas.getContext("2d");
        try {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const img = document.createElement("img");
          img.src = canvas.toDataURL("image/jpeg", 0.7);
          img.alt = file.name;
          img.style.cssText = "width:100%;height:100%;object-fit:cover;position:absolute;inset:0;";
          thumb.insertBefore(img, thumb.firstChild);
        } catch (_) {
          // silent fail on CORS/decoding edge cases
        } finally {
          cleanup();
        }
      },
      { once: true },
    );

    video.addEventListener("error", cleanup, { once: true });
  }

  #toast(msg, type = "info") {
    if (typeof window.showToast === "function") {
      window.showToast({ message: msg, type });
    } else {
      console.warn(`[upload-preview] ${type}: ${msg}`);
    }
  }

  #fmtBytes(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  #fmtSec(seconds) {
    if (seconds < 60) return `${Math.round(seconds * 10) / 10}s`;
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}m ${s}s`;
  }

  #humanize(str) {
    if (!str || str === "—") return str;
    return str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }

  #esc(str) {
    if (!str || str === "—") return str;
    const d = document.createElement("div");
    d.textContent = str;
    return d.innerHTML;
  }

  #nextBtnEl() {
    return this.element.querySelector("[data-project-wizard-target='nextBtn']");
  }

  #setSubmitLabel(text) {
    const btn = this.#nextBtnEl();
    if (btn) btn.textContent = text;
  }

  // ── Direct upload ─────────────────────────────────────────────────────────

  #uploadFile(file, url) {
    const key = this.#key(file);
    this.#failedKeys.delete(key); // clear any previous failure

    const upload = new DirectUpload(file, url, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener("progress", (ev) => {
          if (!ev.lengthComputable) return;
          this.#updateProgress(file, Math.round((ev.loaded / ev.total) * 100));
        });
      },
    });

    upload.create((error, blob) => {
      if (error) {
        console.error("Direct upload failed:", error);
        this.#failedKeys.add(key);
        this.#updateProgress(file, "error");
      } else {
        this.#failedKeys.delete(key);
        this.#blobIds.set(key, blob.signed_id);
        this.#updateProgress(file, 100);
      }
    });
  }

  #updateProgress(file, pct) {
    const item = this.assetsPreviewTarget?.querySelector(`[data-file-key="${this.#key(file)}"]`);
    const ring = item?.querySelector(".filmstrip__progress");
    if (!ring) return;

    if (pct === 100) {
      ring.hidden = true;
      item.classList.remove("filmstrip__item--error");
    } else if (pct === "error") {
      ring.hidden = true;
      item.classList.add("filmstrip__item--error");
      // Clicking the failed tile retries the upload
      if (!item.dataset.retryBound) {
        item.dataset.retryBound = "1";
        item.addEventListener("click", (e) => {
          if (e.target.closest(".filmstrip__remove")) return;
          const url = this.assetsInputTarget?.dataset?.directUploadUrl;
          if (url) this.#uploadFile(file, url);
        });
      }
    } else {
      ring.hidden = false;
      ring.style.setProperty("--pct", pct + "%");
      item.classList.remove("filmstrip__item--error");
    }
  }

  #injectBlobInputs() {
    this.element.querySelectorAll("input[data-blob-input]").forEach((el) => el.remove());
    // One ordered array carries both sources, so the order the user dragged
    // into survives to the server: a signed blob id for an upload, a
    // "brand:<id>" token for a gallery pick.
    const hidden = (name, value) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      input.dataset.blobInput = "true";
      this.element.appendChild(input);
    };

    this.#assetFiles.forEach((file) => {
      let value;
      if (this.#isBrand(file)) value = `brand:${file.brandAssetId}`;
      else if (this.#isExisting(file)) value = `existing:${file.projectAssetId}`;
      else value = this.#blobIds.get(this.#key(file));
      if (!value) return;

      hidden("project[assets][]", value);
    });

    this.#removedExistingIds.forEach((id) => hidden("project[remove_asset_ids][]", id));
  }

  #waitForUploads() {
    return new Promise((resolve, reject) => {
      const deadline = Date.now() + 5 * 60 * 1000; // 5 min max wait
      const check = () => {
        if (this.#failedKeys.size > 0) {
          reject(this.i18nErrorUploadFailedValue);
          return;
        }
        const allDone = this.#assetFiles.every((f) => this.#isStored(f) || this.#blobIds.has(this.#key(f)));
        if (allDone) {
          resolve();
        } else if (Date.now() > deadline) {
          reject(this.i18nErrorTimeoutValue);
        } else {
          setTimeout(check, 400);
        }
      };
      check();
    });
  }
}
